import { useCallback, useEffect, useState } from "react";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { resolveEffectiveClientId } from "utils/effectiveClientId";

function mapSlaveRows(data) {
  const rows = Array.isArray(data?.accounts) ? data.accounts : [];
  return rows.map((a) => ({
    id: a.id || `slot-${a.slot}`,
    slot: Number(a.slot),
    accountType: a.accountType || "MT5",
    accountNumber: a.accountNumber || "",
    server: a.server || "",
    copierDisplayName: a.copierDisplayName || "",
    tradeCopierAccountId: a.tradeCopierAccountId ?? null,
    tradingPasswordMasked: a.tradingPasswordMasked || "••••••••••",
    linkedToCopier: a.linkedToCopier === true,
    brokerConnected: a.brokerConnected === true,
    brokerState: a.brokerState ?? null,
    balanceDisplay: a.balanceDisplay ?? null,
    credentialsHint: a.credentialsHint ?? null,
  }));
}

/**
 * Progressive load: show accounts as soon as list returns, chart loads after.
 */
export function useClientDashboardData(session) {
  const [accounts, setAccounts] = useState([]);
  const [perfAccounts, setPerfAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perfLoading, setPerfLoading] = useState(false);
  const [error, setError] = useState("");

  const effectiveClientId = resolveEffectiveClientId(session);

  const refresh = useCallback(async () => {
    if (!effectiveClientId) {
      setLoading(false);
      setPerfLoading(false);
      setAccounts([]);
      setPerfAccounts([]);
      setError("");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const slavesRes = await fetch(apiUrl("/api/client/copy-list-slaves"), {
        credentials: "include",
      });
      const slavesCt = slavesRes.headers.get("content-type") || "";
      if (!slavesCt.includes("application/json")) {
        throw new Error(
          "Service temporarily unavailable. Please refresh the page in a moment.",
        );
      }
      const slavesData = await slavesRes.json().catch(() => ({}));
      if (!slavesRes.ok || !slavesData.ok) {
        throw new Error(
          clientSafeMessage(
            slavesData.message,
            "Could not load your trading accounts.",
          ),
        );
      }

      const rows = mapSlaveRows(slavesData);
      setAccounts(rows);
      // Accounts visible immediately — do not wait for the chart.
      setLoading(false);

      setPerfLoading(true);
      try {
        const perfRes = await fetch(
          apiUrl("/api/client/copy-performance-history"),
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fast: true,
              slaves: rows.map((s) => ({
                slot: s.slot,
                copierDisplayName: s.copierDisplayName,
                tradeCopierAccountId: s.tradeCopierAccountId,
                brokerConnected: s.brokerConnected,
                balanceDisplay: s.balanceDisplay,
              })),
            }),
          },
        );
        const perfData = await perfRes.json().catch(() => ({}));
        if (!perfRes.ok || !perfData.ok) {
          throw new Error(
            clientSafeMessage(
              perfData.message,
              "Could not load your performance chart.",
            ),
          );
        }
        setPerfAccounts(
          Array.isArray(perfData.accounts) ? perfData.accounts : [],
        );
      } catch (perfErr) {
        // Keep accounts; only chart failed.
        setPerfAccounts([]);
        setError(
          clientSafeMessage(
            perfErr?.message,
            "Accounts loaded; chart is temporarily unavailable.",
          ),
        );
      } finally {
        setPerfLoading(false);
      }
    } catch (e) {
      setError(clientSafeMessage(e?.message, "Could not load your dashboard."));
      // Do not wipe previously visible accounts on a transient list failure.
      setLoading(false);
      setPerfLoading(false);
    }
  }, [effectiveClientId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    accounts,
    perfAccounts,
    loading,
    perfLoading,
    error,
    refresh,
    effectiveClientId,
  };
}
